"use client";

import { type RefObject, useCallback, useEffect, useState } from "react";

const useScrollEdges = (scrollRef: RefObject<HTMLDivElement | null>) => {
  const [canScrollToStart, setCanScrollToStart] = useState(false);
  const [canScrollToEnd, setCanScrollToEnd] = useState(false);

  const updateScrollState = useCallback(() => {
    const container = scrollRef.current;
    if (!container) {
      return;
    }
    // RTL reports scrollLeft as 0 down to -(max), so abs() is distance scrolled.
    const distanceScrolled = Math.abs(container.scrollLeft);
    const maxScroll = container.scrollWidth - container.clientWidth;
    setCanScrollToStart(distanceScrolled > 1);
    setCanScrollToEnd(distanceScrolled < maxScroll - 1);
  }, [scrollRef]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) {
      return;
    }
    updateScrollState();
    container.addEventListener("scroll", updateScrollState, { passive: true });
    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(updateScrollState)
        : null;
    resizeObserver?.observe(container);
    return () => {
      container.removeEventListener("scroll", updateScrollState);
      resizeObserver?.disconnect();
    };
  }, [scrollRef, updateScrollState]);

  return { canScrollToStart, canScrollToEnd };
};

export default useScrollEdges;
